import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { DropdownBankInput, DropdownInvestmentInput } from 'src/app/shared/money-dropdown-select/money-dropdown-select';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ClientBankAccountOut } from 'src/app/services/rest-api/rest-api';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { ModalService } from 'src/app/services/modal/modal.service';
import { WithdrawModalInput } from 'src/app/services/modal/modal';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  templateUrl: './money-withdraw.component.html',
  styleUrls: ['./money-withdraw.component.scss'],
  standalone: false
})
export class MoneyWithdrawComponent implements OnInit, OnDestroy {
  public availableBankAccounts: DropdownBankInput[] = [];
  public availableInvestments: DropdownInvestmentInput[] = [];

  public preloader = true;

  private routeSub?: Subscription;

  public selectedInvestmentMinWithdraw = NaN;
  private formSubscription?: Subscription;

  public get selectedInvestmentMaxWithdraw(): number {
    return this.selectedInvestment.actualValue - this.selectedInvestment.sumOfPendingWithdrawals;
  }

  public withdrawForm = new UntypedFormGroup({
    amount: new UntypedFormControl('0'),
    investmentId: new UntypedFormControl(''),
    clientBankAccountId: new UntypedFormControl(''),
  });

  private get amount(): number {
    const formAmount = this.withdrawForm.value['amount'];
    if(!formAmount || formAmount === 0) {
      return 0;
    }

    return +String(formAmount).replaceAll(' ', '');
  }

  public get selectedInvestment(): DropdownInvestmentInput {
    return this.availableInvestments.find(investment => investment.id === this.withdrawForm.value['investmentId']) as DropdownInvestmentInput;
  }

  private get selectedBankAccountNumber(): string {
    const selected = this.availableBankAccounts.find(account => account.id === this.withdrawForm.value['clientBankAccountId'])
    return selected!.accountNumber;
  }

  public constructor(
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private modalService: ModalService,
    private router: Router
  ) { }

  public ngOnInit(): void {
    this.initNewWithdrawSub();
    this.loadData();
  }

  private loadData(): void {

    this.restApiService.getPortfoliosAndStrategies().subscribe({
        error: err => this.handleInitError(err),
        next: res => this.handleInvestmentsData(res),
    });
  }

  private initFormvalueSubscribe() {
    this.formSubscription = this.withdrawForm.valueChanges.pipe(
      distinctUntilChanged((prev, curr) =>
        prev.investmentId === curr.investmentId
      )
  ).subscribe(() => this.loadBankAccounts());
  }

  private handleInvestmentsData(availableInvestments: ExpectedOwnedStrategy[]) {
    if(!availableInvestments.length) {
      this.preloader = false;
      return;
    }

    this.availableInvestments.push(
      ...availableInvestments.map(investment => {
        return {
          name: investment.name,
          type: investment.portfolioType.toLocaleLowerCase() as 'strategy' | 'portfolio',
          actualValue: investment.sumAmount,
          currency: investment.currency,
          id: investment.portfolioId + '',
          containsWithdrawAllRequest: investment.sumOfPendingWithdrawals.containsWithdrawAllRequest,
          sumOfPendingWithdrawals: investment.sumOfPendingWithdrawals.amount,
          valueWithoutPendingWithdrawals: parseInt(investment.sumAmount - investment.sumOfPendingWithdrawals.amount + ''),
          dip: investment.dip,
          minWithdrawalAmount: investment.minWithdrawalAmount,
          efektika: investment.efektika,
        }
      }),
    );

    if (this.selectedInvestment) {
      this.withdrawForm.patchValue({ investmentId: this.selectedInvestment.id });
      this.selectedInvestmentMinWithdraw = this.selectedInvestment.minWithdrawalAmount;
    } else {
      this.withdrawForm.patchValue({ investmentId: this.availableInvestments[0].id });
      this.selectedInvestmentMinWithdraw = this.availableInvestments[0].minWithdrawalAmount;
    }


    this.initFormvalueSubscribe();
    this.loadBankAccounts();
  }

  private handleInitError(error: unknown) {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private loadBankAccounts(): void {
    this.selectedInvestmentMinWithdraw = this.selectedInvestment.minWithdrawalAmount
    this.restApiService.getAvailableBankAccounts(this.selectedInvestment.id).subscribe({
      error: this.handleBankAccountsError.bind(this),
      next: this.handleBankAccountsData.bind(this),
    });
  }

  private handleBankAccountsData(bankAccounts: ClientBankAccountOut[]): void {
    if(bankAccounts.length === 0) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.noBankAccountsError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      this.preloader = false;
      return;
    }

    this.availableBankAccounts = bankAccounts.map((account) => {
      const accountNumber = account.accountNumber + '/' + account.bankCode;
      return {
        name: accountNumber,
        accountNumber: accountNumber,
        id: account.clientBankAccountId,
      }
    });

    this.withdrawForm.patchValue({ clientBankAccountId: this.availableBankAccounts[0].id });
    this.preloader = false;
  }

  private handleBankAccountsError(error: unknown): void {
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public withdraw(all?: true): void {
    this.withdrawForm.controls['amount'].markAsTouched();

    if(!this.validateForm(all)) {
      return;
    }

    const modalOptions: WithdrawModalInput = {
      investmentType: this.selectedInvestment.type,
      investmentId: this.selectedInvestment.id,
      name: this.selectedInvestment.name,
      amount: this.amount,
      currency: this.selectedInvestment.currency,
      clientBankAccountId: this.withdrawForm.value['clientBankAccountId'] as number,
      bankAccount: this.selectedBankAccountNumber,
      modalType: 'UNDER-LIMIT',
      valueWithoutPendingDeposits: this.selectedInvestment.valueWithoutPendingWithdrawals,
      dip: this.selectedInvestment.dip,
      efektika: !!this.selectedInvestment.efektika,
    }

    if(all || modalOptions.amount === modalOptions.valueWithoutPendingDeposits) {
      modalOptions.modalType = 'ALL';
      modalOptions.amount = modalOptions.valueWithoutPendingDeposits;
    }

    if(modalOptions.modalType === 'ALL') {
      this.modalService.openWithdrawModal(modalOptions);
    } else {
      this.modalService.openWithdrawConsultOfferModal(modalOptions);
    }
  }

  private validateForm(withdrawingAll?: boolean): boolean {
    if(this.selectedInvestment.containsWithdrawAllRequest) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.liquidationWithdrawalsError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }
    if(this.selectedInvestment.actualValue === 0) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.nothingToWithdraw',
        message: 'toastNotifications.messages.nothingToWithdraw',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }
    if(withdrawingAll) {
      return true;
    }
    if(this.amount === 0) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.withdrawZeroError',
        message: 'toastNotifications.messages.withdrawZeroError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }
    if(this.amount > this.selectedInvestmentMaxWithdraw) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.withdrawLimitExceed',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }
    if(this.selectedInvestmentMinWithdraw > this.amount) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.tooSmallWithdrawError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return false;
    }

    return true;
  }

  private initNewWithdrawSub(): void {
    this.routeSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // on withdraw reload ongoing payments
      this.preloader = true;
      this.availableBankAccounts.length = 0;
      this.availableInvestments.length = 0;
      this.loadData();
    });
  }

  public ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if(this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
}
